import { useState } from "react";

import { Box, Wrap, WrapItem } from "@chakra-ui/react";
import { isEmpty, isNil } from "lodash";
import { z } from "zod";

import { Tag } from "@/components/display";
import {
  Editable,
  EditableField,
  EditablePreview,
  Option,
  Select,
  useIsDirty,
} from "@/components/form";
import { useDebouncedState } from "@/hooks";
import { asOptions, useRegions } from "@/services/hooks/typeahead";

import { EditableFieldError } from "./editable-field-error";
import { EditableFieldLabel } from "./editable-field-label";
import { EditableFieldProps } from "./types";

export const RegionsEditableField = ({
  defaultValue,
  isReadOnly = false,
  onChange,
  onSave,
  onCancel,
}: EditableFieldProps<Option[]>) => {
  const [nextValue, setNextValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);

  const isDirty = useIsDirty(
    defaultValue?.map((x) => x.value).sort(),
    nextValue?.map((x) => x.value).sort()
  );

  const [searchQuery, debouncedSearchQuery, setSearchQuery] =
    useDebouncedState("");

  const { data: regions = [], isLoading: isLoadingRegions } = useRegions(
    { search: debouncedSearchQuery },
    { select: asOptions }
  );

  return (
    <Box w="full">
      <EditableFieldLabel isDirty={isDirty}>Region</EditableFieldLabel>

      <Editable
        label="Region"
        isReadOnly={isReadOnly}
        isDisabled={!isNil(error)}
        hasValue={!isEmpty(defaultValue)}
        onSave={async () => {
          await onSave?.(nextValue);
        }}
        onCancel={() => {
          setNextValue(defaultValue);
          onCancel?.();
        }}
      >
        <EditablePreview>
          <Wrap spacing={1}>
            {defaultValue?.map((item) => (
              <WrapItem key={item.value}>
                <Tag>{item.label}</Tag>
              </WrapItem>
            ))}
          </Wrap>
        </EditablePreview>

        <EditableField>
          <Select
            defaultValue={defaultValue}
            value={nextValue}
            options={regions}
            inputValue={searchQuery}
            placeholder="Search for regions..."
            tagVariant="outline"
            isMulti
            isLoading={isLoadingRegions}
            onSearchQueryChange={(value) => {
              setSearchQuery(value);
            }}
            onChange={(value) => {
              schema.safeParseAsync(value).then((result) => {
                if (result.success) {
                  setError(null);
                } else {
                  setError(result.error.issues[0].message);
                }
              });

              setNextValue(value);
              onChange?.(value);
            }}
          />

          <EditableFieldError error={error} />
        </EditableField>
      </Editable>
    </Box>
  );
};

const schema = z
  .array(z.object({ value: z.string(), label: z.string() }))
  .nonempty("You must select at least one value");
